import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import { UserSelector } from 'components/AutocompletePopup'
import type { MentionItem } from './DualMentionExtension'
import mentionStyles from '../mentions.module.scss'

interface MentionListRef {
    onKeyDown: (props: { event: KeyboardEvent }) => boolean
}

interface MentionListProps {
    items: MentionItem[]
    command: (item: MentionItem) => void
    editor: unknown
}

const MentionList = forwardRef<MentionListRef, MentionListProps>((props, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0)

    const referenceLabel = (item: MentionItem) =>
        (item.content || item.value || '').replace(/^@+/, '')

    const selectItem = (index: number) => {
        const item = props.items[index]
        if (item && !item.isNoResults) {
            props.command(item)
        }
    }

    const upHandler = () => {
        setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length)
    }

    const downHandler = () => {
        setSelectedIndex((selectedIndex + 1) % props.items.length)
    }

    const enterHandler = () => {
        selectItem(selectedIndex)
    }

    useEffect(() => setSelectedIndex(0), [props.items])

    useImperativeHandle(ref, () => ({
        onKeyDown: ({ event }: { event: KeyboardEvent }) => {
            if (event.key === 'ArrowUp') {
                upHandler()
                return true
            }

            if (event.key === 'ArrowDown') {
                downHandler()
                return true
            }

            if (event.key === 'Enter' || event.key === 'Tab') {
                enterHandler()
                return true
            }

            return false
        },
    }))

    return (
        <div className={mentionStyles.mentionList}>
            {props.items.length ? (
                props.items.map((item, index) => (
                    <div
                        className={`${mentionStyles.mentionItem} ${
                            index === selectedIndex ? mentionStyles.selected : ''
                        }`}
                        key={item.id || index}
                        onMouseDown={event => {
                            event.preventDefault()
                        }}
                        onClick={event => {
                            event.stopPropagation()
                            selectItem(index)
                        }}
                    >
                        {item.isNoResults ? (
                            <div className={mentionStyles.noResults}>{item.content}</div>
                        ) : item.type === 'reference' ? (
                            <div className={mentionStyles.referenceItem} data-item-type="reference">
                                <span className={mentionStyles.referencePreview}>
                                    {item.previewUrl && item.kind !== 'audio' ? (
                                        <img
                                            src={item.previewUrl}
                                            alt=""
                                            className={mentionStyles.referenceThumb}
                                        />
                                    ) : (
                                        <span className={mentionStyles.referenceFallback}>
                                            {item.kind === 'video'
                                                ? 'V'
                                                : item.kind === 'audio'
                                                ? 'A'
                                                : 'I'}
                                        </span>
                                    )}
                                </span>
                                <span className={mentionStyles.referenceContent}>
                                    <span className={mentionStyles.modelName}>
                                        @{referenceLabel(item)}
                                    </span>
                                    {item.meta && (
                                        <span className={mentionStyles.referenceMeta}>
                                            {item.meta}
                                        </span>
                                    )}
                                </span>
                            </div>
                        ) : item.triggerType === 'model' ? (
                            // Model mention display
                            <div className={mentionStyles.modelItem} data-item-type="model">
                                <span className={mentionStyles.modelPrefix}>@@</span>
                                <span className={mentionStyles.modelName}>{item.content}</span>
                            </div>
                        ) : (
                            // User mention display
                            <UserSelector user={item} />
                        )}
                    </div>
                ))
            ) : (
                <div className={mentionStyles.mentionItem}>No results</div>
            )}
        </div>
    )
})

MentionList.displayName = 'MentionList'

export default MentionList
